import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type FavoritesContextType = {
  /** Increment this when favorites list changed (add/remove) so consumers re-read from storage. */
  favoritesVersion: number;
  /** Call after adding or removing a favorite so all AddFavorite, RemoveFavorite, and Favorites refresh. */
  invalidateFavorites: () => void;
};

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

type FavoritesProviderProps = {
  children: React.ReactNode;
};

export function FavoritesProvider({ children }: FavoritesProviderProps): React.ReactElement {
  const [favoritesVersion, setFavoritesVersion] = useState(0);
  const invalidateFavorites = useCallback(() => {
    setFavoritesVersion((v) => v + 1);
  }, []);

  const value = useMemo(
    () => ({ favoritesVersion, invalidateFavorites }),
    [favoritesVersion, invalidateFavorites]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavoritesContext(): FavoritesContextType {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error("useFavoritesContext must be used within a FavoritesProvider");
  }
  return context;
}
